import React, { useState, useEffect } from 'react';
import { Bell, Filter, ExternalLink, Search, Clock, TrendingUp, TrendingDown, BadgeDollarSign } from 'lucide-react';
import { Token } from '../../types/supabase';

// Whale Alert transaction type
interface WhaleTransaction {
  id: string;
  type: 'buy' | 'sell' | 'transfer' | 'liquidity_add' | 'liquidity_remove';
  token: {
    symbol: string;
    name: string;
    mint: string;
    image?: string;
  };
  amount: number;
  amountUsd: number;
  sender: string;
  receiver: string;
  timestamp: number;
  signature: string;
}

interface WhaleAlertsProps {
  selectedToken?: Token;
  onClose?: () => void;
  isWidget?: boolean;
}

const WhaleAlerts: React.FC<WhaleAlertsProps> = ({ selectedToken, onClose, isWidget = false }) => {
  const [transactions, setTransactions] = useState<WhaleTransaction[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [filter, setFilter] = useState<'all' | 'buy' | 'sell' | 'liquidity'>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [minAmount, setMinAmount] = useState<number>(1000); // Minimum USD amount to filter
  const [notificationsEnabled, setNotificationsEnabled] = useState<boolean>(false);

  // Generate mock whale transactions
  useEffect(() => {
    const generateMockWhaleData = () => {
      const mockData: WhaleTransaction[] = [];
      const transactionTypes: ('buy' | 'sell' | 'transfer' | 'liquidity_add' | 'liquidity_remove')[] = 
        ['buy', 'sell', 'transfer', 'liquidity_add', 'liquidity_remove'];
      
      const tokens: {symbol: string; name: string; mint: string; image?: string}[] = [
        { symbol: 'SOL', name: 'Solana', mint: 'So11111111111111111111111111111111111111112' },
        { symbol: 'BONK', name: 'Bonk', mint: 'DezXAZ8z7PnrnRJjz3wXBoRgixCa6xjnB7YaB1pPB263' },
        { symbol: 'JUP', name: 'Jupiter', mint: 'JUPyiwrYJFskUPiHa7hkeR8VUtAeFoSYbKedZNsDvCN' }
      ];
      
      // If we have a selected token, include it in the mock data
      if (selectedToken) {
        tokens.push({
          symbol: selectedToken.symbol || 'UNKNOWN',
          name: selectedToken.name || 'Unknown Token',
          mint: selectedToken.mint || '',
          image: selectedToken.image_uri || undefined
        });
      }
      
      // Generate 20 random transactions
      for (let i = 0; i < 20; i++) {
        const randomToken = tokens[Math.floor(Math.random() * tokens.length)];
        const randomType = transactionTypes[Math.floor(Math.random() * transactionTypes.length)];
        const randomAmount = Math.floor(Math.random() * 1000000) + 10000; // Between 10K and 1M
        const randomUsdAmount = randomAmount / 100; // Just for mock data
        
        // Random wallet addresses
        const randomAddress1 = `${Math.random().toString(36).substring(2, 10)}...${Math.random().toString(36).substring(2, 6)}`;
        const randomAddress2 = `${Math.random().toString(36).substring(2, 10)}...${Math.random().toString(36).substring(2, 6)}`;
        
        // Random signature
        const randomSignature = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
        
        // Random timestamp within the last 24 hours
        const now = new Date();
        const randomTime = now.getTime() - Math.floor(Math.random() * 24 * 60 * 60 * 1000);
        
        mockData.push({
          id: `tx-${i}`,
          type: randomType,
          token: randomToken,
          amount: randomAmount,
          amountUsd: randomUsdAmount,
          sender: randomAddress1,
          receiver: randomAddress2,
          timestamp: randomTime,
          signature: randomSignature
        });
      }
      
      // Sort by timestamp (newest first)
      mockData.sort((a, b) => b.timestamp - a.timestamp);
      
      return mockData;
    };
    
    setIsLoading(true);
    // Simulate API fetch delay
    setTimeout(() => {
      const mockData = generateMockWhaleData();
      setTransactions(mockData);
      setIsLoading(false);
    }, 1000);
  }, [selectedToken]);
  
  // Filter transactions based on current filter and search term
  const filteredTransactions = transactions.filter(tx => {
    // Filter by type
    if (filter === 'buy' && tx.type !== 'buy') return false;
    if (filter === 'sell' && tx.type !== 'sell') return false;
    if (filter === 'liquidity' && !tx.type.includes('liquidity')) return false;
    
    // Filter by amount
    if (tx.amountUsd < minAmount) return false;
    
    // Filter by search term
    if (searchTerm && !tx.token.symbol.toLowerCase().includes(searchTerm.toLowerCase()) && 
        !tx.token.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !tx.sender.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !tx.receiver.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    // Filter by selected token
    if (selectedToken && tx.token.mint !== selectedToken.mint) return false;
    
    return true;
  });
  
  const formatTime = (timestamp: number): string => {
    const now = new Date();
    const txTime = new Date(timestamp);
    const diffMs = now.getTime() - txTime.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };
  
  const formatAmount = (amount: number): string => {
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(2)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(2)}K`;
    return `$${amount.toFixed(2)}`;
  };
  
  const getTransactionTypeColor = (type: string): string => {
    switch (type) {
      case 'buy': return 'bg-green-500/20 text-green-400';
      case 'sell': return 'bg-red-500/20 text-red-400';
      case 'transfer': return 'bg-blue-500/20 text-blue-400';
      case 'liquidity_add': return 'bg-purple-500/20 text-purple-400';
      case 'liquidity_remove': return 'bg-yellow-500/20 text-yellow-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };
  
  const getTransactionTypeText = (type: string): string => {
    switch (type) {
      case 'buy': return 'Buy';
      case 'sell': return 'Sell';
      case 'transfer': return 'Transfer';
      case 'liquidity_add': return 'Add Liquidity';
      case 'liquidity_remove': return 'Remove Liquidity';
      default: return type;
    }
  };
  
  const getTransactionIcon = (type: string) => {
    switch (type) {
      case 'buy': return <TrendingUp size={14} className="text-green-400" />;
      case 'sell': return <TrendingDown size={14} className="text-red-400" />;
      case 'transfer': return <ExternalLink size={14} className="text-blue-400" />;
      case 'liquidity_add': return <TrendingUp size={14} className="text-purple-400" />;
      case 'liquidity_remove': return <TrendingDown size={14} className="text-yellow-400" />;
      default: return <ExternalLink size={14} className="text-gray-400" />;
    }
  };

  const toggleNotifications = () => {
    setNotificationsEnabled(!notificationsEnabled);
  };
  
  const customStyles = `
    .glass-input {
      background-color: rgba(12, 20, 26, 0.26) !important;
      backdrop-filter: blur(6px);
      -webkit-backdrop-filter: blur(6px);
      border: 1px solid rgba(255, 255, 255, 0.1);
    }
    
    .glass-input:focus {
      border-color: #47be8f;
      outline: none;
      box-shadow: 0 0 0 1px rgba(12, 20, 26, 0.26);
    }
    
    .filter-button {
      background-color: rgba(12, 20, 26, 0.4);
      border: 1px solid rgba(255, 255, 255, 0.1);
    }
    
    .filter-button.active {
      background-color: rgba(71, 190, 143, 0.2);
      border: 1px solid rgba(71, 190, 143, 0.4);
      color: #47be8f;
    }
  `;
  
  const content = (
    <div className="w-full h-full flex flex-col bg-transparent" data-widget-type="whale-alerts">
      <style>{customStyles}</style>
      
      {/* Header with title and filters */}
      <div className="p-3 border-b border-white/10">
        <div className="flex justify-between items-center mb-3">
          <h3 className="text-sm font-medium text-white flex items-center">
            {/* Replace the problematic icon with a standard one */}
            <BadgeDollarSign size={16} className="mr-2 text-[#47be8f]" />
            Whale Alerts
            {selectedToken && (
              <span className="ml-2 text-xs text-gray-400">
                for {selectedToken.symbol}
              </span>
            )}
          </h3>
          
          <div className="flex items-center">
            <button
              onClick={toggleNotifications}
              className={`p-1.5 rounded-full transition-colors ${
                notificationsEnabled ? 'bg-[#47be8f]/20 text-[#47be8f]' : 'text-gray-400 hover:text-gray-300'
              }`}
              title={notificationsEnabled ? 'Notifications enabled' : 'Enable notifications'}
            >
              <Bell size={16} />
            </button>
          </div>
        </div>
        
        {/* Search and filter controls */}
        <div className="flex flex-col space-y-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by token or address"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 text-sm glass-input rounded text-black"
            />
          </div>
          
          <div className="flex justify-between items-center">
            <div className="flex space-x-1">
              <button
                onClick={() => setFilter('all')}
                className={`filter-button px-2 py-1 text-xs rounded ${filter === 'all' ? 'active' : 'text-white/70'}`}
              >
                All
              </button>
              <button
                onClick={() => setFilter('buy')}
                className={`filter-button px-2 py-1 text-xs rounded ${filter === 'buy' ? 'active' : 'text-white/70'}`}
              >
                Buys
              </button>
              <button
                onClick={() => setFilter('sell')}
                className={`filter-button px-2 py-1 text-xs rounded ${filter === 'sell' ? 'active' : 'text-white/70'}`}
              >
                Sells
              </button>
              <button
                onClick={() => setFilter('liquidity')}
                className={`filter-button px-2 py-1 text-xs rounded ${filter === 'liquidity' ? 'active' : 'text-white/70'}`}
              >
                Liquidity
              </button>
            </div>
            
            <div className="flex items-center">
              <Filter size={12} className="text-gray-400 mr-1" />
              <select
                value={minAmount}
                onChange={(e) => setMinAmount(Number(e.target.value))}
                className="text-xs glass-input rounded text-black py-1 px-1"
              >
                <option value={100}>$100+</option>
                <option value={1000}>$1K+</option>
                <option value={10000}>$10K+</option>
                <option value={100000}>$100K+</option>
                <option value={1000000}>$1M+</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      
      {/* Transaction list */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#47be8f]"></div>
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            {/* Replace the problematic icon with a standard one */}
            <BadgeDollarSign size={32} className="mb-2" />
            <p className="text-sm">No whale transactions found</p>
            {searchTerm || filter !== 'all' || (selectedToken && selectedToken.mint) ? (
              <button 
                onClick={() => {
                  setSearchTerm('');
                  setFilter('all');
                }} 
                className="mt-2 text-xs text-[#47be8f] hover:underline"
              >
                Clear filters
              </button>
            ) : null}
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {filteredTransactions.map((tx) => (
              <div key={tx.id} className="p-3 hover:bg-white/5">
                <div className="flex justify-between items-start">
                  <div className="flex items-start">
                    <div className="mr-3 pt-0.5">
                      {getTransactionIcon(tx.type)}
                    </div>
                    <div>
                      <div className="flex items-center">
                        <span className="font-medium text-white">
                          {tx.token.symbol}
                        </span>
                        <span className={`text-xs px-1.5 py-0.5 rounded-sm ml-2 ${getTransactionTypeColor(tx.type)}`}>
                          {getTransactionTypeText(tx.type)}
                        </span>
                      </div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        From: {tx.sender}
                      </div>
                      {tx.type !== 'transfer' && (
                        <div className="text-xs text-gray-400">
                          To: {tx.receiver}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="font-medium text-white">
                      {formatAmount(tx.amountUsd)}
                    </div>
                    <div className="flex items-center justify-end text-xs text-gray-400 mt-0.5">
                      <Clock size={10} className="mr-1" />
                      {formatTime(tx.timestamp)}
                    </div>
                  </div>
                </div>
                
                <div className="mt-1.5">
                  <a 
                    href={`https://solscan.io/tx/${tx.signature}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs text-[#47be8f] hover:text-[#39a677] transition-colors flex items-center"
                  >
                    View Transaction <ExternalLink size={10} className="ml-1" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // If this is being rendered as a widget, return just the content
  if (isWidget) {
    return (
      <div className="absolute inset-0 m-0 p-0 overflow-hidden">
        {content}
      </div>
    );
  }

  // Otherwise, return the modal version with container
  return (
    <div className="p-4 bg-dark-grey border border-gray-800 rounded-lg">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {/* Replace the problematic icon with a standard one */}
          <BadgeDollarSign size={18} className="text-primary" />
          <h2 className="text-lg font-bold text-white">Whale Alerts</h2>
        </div>
        {onClose && (
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-white"
          >
            &times;
          </button>
        )}
      </div>
      
      {content}
    </div>
  );
};

// Factory function to create a canvas widget
export const createWhaleAlertsWidget = () => {
  return {
    type: 'whale-alerts',
    title: 'Whale Alerts',
    content: <WhaleAlerts isWidget={true} />,
    size: { width: 450, height: 600 },
    padding: 0,
    margin: 0,
    contentStyle: { 
      padding: 0, 
      margin: 0,
      backgroundColor: 'transparent',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      overflow: 'hidden'
    },
    disablePadding: true,
    fullWidth: true,
    fullHeight: true,
    background: 'transparent',
    glassMorphism: true,
    containerClassName: 'p-0 m-0 overflow-hidden',
    containerStyle: {
      backgroundColor: 'rgba(255, 255, 255, 0.95)',
      backdropFilter: 'blur(12px)',
      borderRadius: '12px',
      border: '1px solid rgba(36, 195, 91, 0.2)',
      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(36, 195, 91, 0.2)',
      overflow: 'hidden'
    }
  };
};

export default WhaleAlerts;
